import Vue from 'vue';
import articlesService from '../../api/articles-service';
import imageService from '../../api/image-service';
import { removeArrayItem, reorderSequenceByIndex, updateArrayItem } from '../../util/array';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';
import { mapErrorsToInputs, scrollToFirstError } from '../../util/forms';

export const initialArticleFilterParams = () => ({});

const state = {
  articles: null,
  articlesPagination: {},
  editedArticle: {},
  articleValidationErrors: {},
  articleFilterParams: initialArticleFilterParams(),
  loadingArticles: false,
  savingArticle: false,
  savingImage: false,
};

const getters = {
  types() {
    return [
      { value: 'news', text: i18n.t('news') },
      { value: 'events', text: i18n.t('events') },
    ];
  },
};

const mutations = {
  SET_ARTICLES(state, { data, current_page, per_page, total }) {
    state.articles = data;
    state.articlesPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.articleFilterParams = params;
  },

  STORE_ARTICLE(state, article) {
    state.articles = [...state.articles, article];
    state.articleValidationErrors = {};
    state.articlesPagination.total += 1;
  },

  SET_EDITED_ARTICLE(state, article) {
    state.articleValidationErrors = {};
    state.editedArticle = JSON.parse(JSON.stringify(article));
  },

  UPDATE_ARTICLE(state, article) {
    for (let i = 0; i < state.articles?.length; i++) {
      if (state.articles[i].id === article.id && state.articles[i].sequence !== article.sequence) {
        state.articles = reorderSequenceByIndex(state.articles, i, article.sequence - 1);
      }
    }
    state.articles = updateArrayItem(state.articles, article);
  },
  REORDER_ARTICLE(state, { oldIndex, newIndex }) {
    state.articles = reorderSequenceByIndex(state.articles, oldIndex, newIndex);
  },

  DELETE_ARTICLE(state, article) {
    state.articles = removeArrayItem(state.articles, article);
    state.articlesPagination.total -= 1;
  },

  SET_ARTICLE_VALIDATION_ERRORS(state, validationErrors) {
    state.articleValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.articleValidationErrors[field];
  },

  SET_LOADING_ARTICLES(state, loading) {
    state.loadingArticles = loading;
  },

  SET_SAVING_ARTICLE(state, saving) {
    state.savingArticle = saving;
  },

  SET_SAVING_IMAGE(state, saving) {
    state.savingImage = saving;
  },

  STORE_ARTICLE_IMAGES(state, images) {
    Vue.set(state.editedArticle, 'images', [...state.editedArticle.images, ...images]);
    for (let i = 0; i < state.articles?.length; i++) {
      if (state.articles[i].id === +images[0]?.model_id) {
        Vue.set(state.articles[i], 'images', [...state.articles[i].images, ...images]);
        break;
      }
    }
  },

  DELETE_ARTICLE_IMAGE(state, image) {
    const updatedImages = state.editedArticle.images.filter((i) => i.id !== image.id);
    Vue.set(state.editedArticle, 'images', updatedImages);
    for (let i = 0; i < state.articles?.length; i++) {
      if (state.articles[i].id === +image.model_id) {
        Vue.set(state.articles[i], 'images', updatedImages);
        break;
      }
    }
  },

  REORDER_ARTICLE_IMAGES(state, { oldIndex, newIndex }) {
    const updatedImages = reorderSequenceByIndex(state.editedArticle.images, oldIndex, newIndex);
    state.editedArticle.images = updatedImages;
    for (let i = 0; i < state.articles.length; i++) {
      if (state.articles[i].id === state.editedArticle.id) {
        state.articles[i].images = updatedImages;
        break;
      }
    }
  },

  SET_ARTICLE_IMAGES(state, images) {
    Vue.set(state.editedArticle, 'images', images);
    for (let i = 0; i < state.articles?.length; i++) {
      if (state.articles[i].id === +images[0]?.model_id) {
        Vue.set(state.articles[i], 'images', images);
        break;
      }
    }
  },
};

const actions = {
  fetchArticles({ commit }, params) {
    commit('SET_LOADING_ARTICLES', true);
    return articlesService
      .getPage(params)
      .then((res) => {
        commit('SET_ARTICLES', res.data);
        if (params.page !== state.articleFilterParams.page) {
          window.scrollTo(0, 0);
        }
        commit('SET_FILTER_PARAMS', params);
      })
      .finally(() => {
        commit('SET_LOADING_ARTICLES', false);
      });
  },

  async storeArticle({ commit }) {
    commit('SET_SAVING_ARTICLE', true);
    const res = await articlesService.create();
    commit('SET_SAVING_ARTICLE', false);
    commit('STORE_ARTICLE', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('article_created'));
    return res;
  },

  editArticle({ state, commit }, articleId) {
    const article = state.articles?.find((a) => a.id === articleId);
    if (article) {
      commit('SET_EDITED_ARTICLE', article);
      return Promise.resolve(article);
    }
    return articlesService.getById(articleId).then((res) => {
      commit('SET_EDITED_ARTICLE', res.data);
    });
  },

  updateArticle({ commit }, article) {
    commit('SET_SAVING_ARTICLE', true);
    return articlesService
      .update(article)
      .then((res) => {
        commit('UPDATE_ARTICLE', {
          ...res.data,
          images: article.images,
        });
        eventBus.$emit(OPEN_SNACKBAR, i18n.t(`${article.type}_updated`));
      })
      .catch((err) => {
        commit('SET_ARTICLE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        scrollToFirstError();
        throw err;
      })
      .finally(() => commit('SET_SAVING_ARTICLE', false));
  },

  reorderArticles({ commit, state }, { oldIndex, newIndex }) {
    const oldArticles = [...state.articles];
    const movedItem = oldArticles[oldIndex];
    commit('REORDER_ARTICLE', { oldIndex, newIndex });
    return articlesService
      .reorder({
        id: movedItem.id,
        type: movedItem.type,
        sequence: state.articles[newIndex].sequence,
      })
      .catch(() => {
        commit('SET_ARTICLES', {
          data: oldArticles,
          current_page: state.articlesPagination.current_page,
          per_page: state.articlesPagination.per_page,
          total: state.articlesPagination.total,
        });
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('reorder_error'));
      });
  },

  deleteArticle({ commit }, article) {
    openConfirmDialog({
      title: i18n.t(`confirm_${article.type || 'entry'}_delete`),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      articlesService.delete(article).then(() => {
        commit('DELETE_ARTICLE', article);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t(`${article.type || 'entry'}_deleted`));
      });
    });
  },

  clearArticleValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },

  async uploadArticleImage({ commit }, { image, type, modelType, modelId }) {
    eventBus.$emit(OPEN_SNACKBAR, {
      text: i18n.t('uploading'),
      showProgress: true,
      timeout: -1,
    });
    commit('SET_SAVING_IMAGE', true);
    const res = await imageService.upload(image, type, modelType, modelId);
    commit('SET_SAVING_IMAGE', false);
    commit('STORE_ARTICLE_IMAGES', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_uploaded'));
    return res;
  },

  deleteArticleImage({ commit }, image) {
    openConfirmDialog({
      title: i18n.t('confirm_article_image_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      imageService.delete(image).then(() => {
        commit('DELETE_ARTICLE_IMAGE', image);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_deleted'));
      });
    });
  },

  reorderArticleImages({ commit }, { newIndex, oldIndex }) {
    const oldImages = JSON.parse(JSON.stringify(state.editedArticle.images));
    const movedItem = oldImages[oldIndex];
    commit('REORDER_ARTICLE_IMAGES', { oldIndex, newIndex });
    return imageService
      .reorderImages({
        id: movedItem.id,
        type: 'image',
        sequence: state.editedArticle.images[newIndex].sequence,
      })
      .catch(() => {
        commit('SET_ARTICLE_IMAGES', oldImages);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('reorder_error'));
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
